import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { eachDayOfInterval, isAfter, isEqual, startOfDay } from 'date-fns';
import classNames from 'classnames';
import { _getDate, getDatesOfMonth, isDateDisabled, isDateSelected } from '../utils';
import { hasValue } from '@utils';
import Tooltip from '@widget/popups/Tooltip';
import tinycolor from 'tinycolor2';
import { UXPContext } from "@core/context/Context";
import Select from '../../Select';
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
const Calendar = (props) => {
    const context = React.useContext(UXPContext);
    // props 
    let { range, dateRange = false, disableWeekEnds, disableDates, minDate, maxDate, onSelect, size, onClickDate, dates, showFullMonthName, specialDates, specialDateBackgroundColor, specialDateTextColor } = props;
    // prepare disabled dates 
    let _disableDates = [];
    disableDates === null || disableDates === void 0 ? void 0 : disableDates.map((date) => {
        _disableDates.push(_getDate(date));
    });
    // get selected dates
    React.useEffect(() => {
        let _range = { start: _getDate(range === null || range === void 0 ? void 0 : range.start), end: _getDate(range === null || range === void 0 ? void 0 : range.end) };
        let selDates = [];
        if (!isDateDisabled(_range.start, _disableDates, disableWeekEnds, minDate, maxDate))
            selDates.push(_range.start);
        if (dateRange) {
            if (_range.start && _range.end) {
                selDates = eachDayOfInterval({ start: _range.start, end: _range.end }).filter((d) => {
                    return !isDateDisabled(d, _disableDates, disableWeekEnds, minDate, maxDate);
                });
            }
        }
        // check if specified dates are in selected dates
        if (dates) {
            let _selDates = selDates.filter((sd) => dates.find((d) => isEqual(startOfDay(d), startOfDay(sd))));
            selDates = _selDates;
        }
        setSelDates(selDates);
        setSelYear(_range.start ? _range.start.getFullYear() : (new Date()).getFullYear());
        setSelMonth(_range.start ? _range.start.getMonth() : (new Date()).getMonth());
    }, [range]);
    // state 
    let [selYear, setSelYear] = React.useState(0);
    let [selMonth, setSelMonth] = React.useState(0);
    let [weeks, setWeeks] = React.useState([]);
    let [selDates, setSelDates] = React.useState([]);
    let [inRangeDates, setInRangeDates] = React.useState([]);
    React.useEffect(() => {
        if (!isNaN(selYear) && !isNaN(selMonth)) {
            let newDate = new Date(selYear, selMonth, 1);
            setWeeks(getDatesOfMonth(newDate));
        }
    }, [selYear, selMonth]);
    const yearOptions = React.useMemo(() => {
        let curYear = (new Date()).getFullYear();
        let years = [];
        for (let i = curYear + 20; i >= curYear - 100; i--) {
            years.push({ label: i.toString(), value: i.toString() });
        }
        return years;
    }, []);
    let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(m => context.$L(`uxp-core.text.${m}`));
    let days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => context.$L(`uxp-core.text.${d}`));
    const monthOptions = React.useMemo(() => {
        return months.map((m, i) => ({
            label: showFullMonthName ? m : m.substring(0, 3),
            value: i.toString()
        }));
    }, [months, showFullMonthName]);
    const handleClickToday = () => {
        handleDateClick(new Date());
    };
    const handleClickNext = () => {
        let _sm = selMonth;
        if (_sm == 11) {
            setSelYear((year) => year + 1);
            setSelMonth(0);
        }
        else {
            setSelMonth((month) => month + 1);
        }
    };
    const handleClickPrev = () => {
        let _sm = selMonth;
        if (_sm == 0) {
            setSelYear((year) => year - 1);
            setSelMonth(11);
        }
        else {
            setSelMonth((month) => month - 1);
        }
    };
    const handleDateClick = (date) => {
        if (onClickDate) {
            onClickDate(date);
        }
        if (dateRange) {
            let _range = { start: _getDate(range === null || range === void 0 ? void 0 : range.start), end: _getDate(range === null || range === void 0 ? void 0 : range.end) };
            if (_range.start && _range.end) {
                _range.start = date;
                _range.end = null;
            }
            else {
                if (_range.start) {
                    if (isAfter(date, _range.start)) {
                        _range.end = date;
                    }
                    else {
                        _range.end = _range.start;
                        _range.start = date;
                    }
                }
                else {
                    _range.start = date;
                    _range.end = null;
                }
            }
            // get dates in range
            let dates = [_range.start];
            if (_range.start && _range.end) {
                dates = eachDayOfInterval({ start: _range.start, end: _range.end }).filter((d) => (!isDateDisabled(d, _disableDates, disableWeekEnds, minDate, maxDate)));
            }
            onSelect(_range, dates);
        }
        else {
            onSelect({ start: date, end: null }, []);
        }
    };
    const handleMouseOver = (date) => {
        if (dateRange) {
            let _range = { start: _getDate(range === null || range === void 0 ? void 0 : range.start), end: _getDate(range === null || range === void 0 ? void 0 : range.end) };
            if (_range.start && !_range.end) {
                _range.end = date;
                if (!isAfter(date, _range.start)) {
                    _range.end = _range.start;
                    _range.start = date;
                }
                let inRange = eachDayOfInterval({ start: _range.start, end: _range.end }).filter((_d) => !isDateDisabled(_d, _disableDates, disableWeekEnds, minDate, maxDate));
                setInRangeDates(inRange);
            }
            else {
                setInRangeDates([]);
            }
        }
    };
    const _specialDates = specialDates === null || specialDates === void 0 ? void 0 : specialDates.map((d) => {
        // Check if `d` is a string, Date, or ISpecialDate
        if (typeof d === "string" || d instanceof Date) {
            return {
                date: _getDate(d),
                label: null,
                backgroundColor: hasValue(specialDateBackgroundColor) ? specialDateBackgroundColor : null,
                color: hasValue(specialDateTextColor) ? specialDateTextColor : null,
            };
        }
        else if (typeof d === "object" && d !== null) {
            return {
                date: _getDate(d.date),
                label: hasValue(d.label) ? d.label : null,
                backgroundColor: hasValue(d.backgroundColor)
                    ? d.backgroundColor
                    : hasValue(specialDateBackgroundColor)
                        ? specialDateBackgroundColor
                        : null,
                color: hasValue(d.color) ? d.color : hasValue(specialDateTextColor) ? specialDateTextColor : null,
            };
        }
        return null;
    }).filter(d => !!d);
    function getSpecialDateProps(date) {
        if (!_specialDates || (_specialDates === null || _specialDates === void 0 ? void 0 : _specialDates.length) == 0)
            return { isSpecialDate: false };
        const inst = _specialDates === null || _specialDates === void 0 ? void 0 : _specialDates.find((d) => isEqual(d.date, date));
        if (!inst)
            return { isSpecialDate: false };
        return {
            isSpecialDate: true,
            bgColor: inst === null || inst === void 0 ? void 0 : inst.backgroundColor,
            textColor: inst === null || inst === void 0 ? void 0 : inst.color,
            label: inst === null || inst === void 0 ? void 0 : inst.label
        };
    }
    function renderDate(date, isSelected, isDisabled, inRange, isSpecialDate, styles) {
        return _jsx("button", { disabled: isDisabled, className: classNames("uxpcore_calendar__date", { "uxpcore_calendar__date--active": isSelected, "uxpcore_calendar__date--in-range": inRange, 'uxpcore_calendar__date--special': isSpecialDate }), onClick: () => handleDateClick(date), onMouseOver: () => handleMouseOver(date), style: styles, children: _jsxs("div", { children: [" ", date === null || date === void 0 ? void 0 : date.getDate()] }) });
    }
    return (_jsxs("div", { className: classNames("uxpcore_calendar", size), children: [_jsxs("div", { className: "uxpcore_calendar__controllers", children: [_jsxs("div", { className: "uxpcore_calendar__controllers-left", children: [_jsx(Select, { options: yearOptions, selected: selYear.toString(), onChange: (val) => setSelYear(parseInt(val)), renderPlaceholder: {
                                    renderCustomPill: () => (_jsx("div", { className: "uxpcore_calendar__pill uxpcore_calendar__pill--year", children: selYear }))
                                } }), _jsx(Select, { options: monthOptions, selected: selMonth.toString(), onChange: (val) => setSelMonth(parseInt(val)), renderPlaceholder: {
                                    renderCustomPill: () => (_jsx("div", { className: "uxpcore_calendar__pill uxpcore_calendar__pill--month", children: months[selMonth] }))
                                } })] }), _jsxs("div", { className: "uxpcore_calendar__controllers-right", children: [_jsx("button", { className: "uxpcore_calendar__navbtn uxpcore_calendar__navbtn--prev", onClick: handleClickPrev, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'chevron-left'] }) }), _jsx("button", { className: "uxpcore_calendar__navbtn uxpcore_calendar__navbtn--today", onClick: handleClickToday, title: context.$L('uxp-core.text.today'), children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'circle'] }) }), _jsx("button", { className: "uxpcore_calendar__navbtn uxpcore_calendar__navbtn--next", onClick: handleClickNext, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'chevron-right'] }) })] })] }), _jsx("div", { className: "uxpcore_calendar__month", children: _jsx("table", { children: _jsxs("tbody", { children: [_jsx("tr", { children: days.map(d => _jsx("th", { children: d })) }), weeks.map((week, key) => {
                                return (_jsx("tr", { children: week.map((w_date, i) => {
                                        var _a;
                                        let isSelected = isDateSelected(w_date, selDates);
                                        let isDisabled = isDateDisabled(w_date, _disableDates, disableWeekEnds, minDate, maxDate);
                                        let inRange = isDateSelected(w_date, inRangeDates);
                                        const { isSpecialDate, bgColor, textColor, label } = getSpecialDateProps(w_date);
                                        const dateStyles = {};
                                        if (isSpecialDate && !isSelected) {
                                            if (hasValue(bgColor))
                                                dateStyles.backgroundColor = bgColor;
                                            if (hasValue(textColor))
                                                dateStyles.color = textColor;
                                            if (isDisabled) {
                                                if (hasValue(bgColor)) {
                                                    const bg = (_a = (tinycolor(bgColor))) === null || _a === void 0 ? void 0 : _a.setAlpha(0.6);
                                                    if (!!bg)
                                                        dateStyles.backgroundColor = '#' + (bg === null || bg === void 0 ? void 0 : bg.toHex8());
                                                }
                                            }
                                        }
                                        return _jsx("td", { children: (isSpecialDate && hasValue(label))
                                                ? _jsx(Tooltip, { content: label, children: renderDate(w_date, isSelected, isDisabled, inRange, isSpecialDate, dateStyles) })
                                                : _jsx(_Fragment, { children: renderDate(w_date, isSelected, isDisabled, inRange, isSpecialDate, dateStyles) }) }, i);
                                    }) }, key));
                            })] }) }) })] }));
};
export default Calendar;
